import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../../assets";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType } from "../../../../../models";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { ResultData } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject } from "../../../../../utils";
import { getDealListingRoute, getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../../../../widget";
import { AvatarCellFormatter } from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";

interface UseWorkflowTaskPanelReturnData {
  handleCloseWorkflowTaskPanel: () => void;
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useTeamTasksPanel = (
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void,
  setShowWorkflowTaskPanel: (show: boolean) => void
): UseWorkflowTaskPanelReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();

  const [dataIsLoading, setDataIsLoading] = useState(true);

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "taskLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 300,
      sortable: true,
    },
    {
      name: "Workflow",
      key: "objectLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 140,
      sortable: true,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
      sortable: true,
    },
    {
      key: "ownerOrganisation.name",
      name: "Owner",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "left",
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "center",
      filterable: true,
      minWidth: 140,
      sortable: true,
    },
    {
      name: "Deadline",
      key: "deadlineDate",
      dataType: "string",
      formatter: "dateOnlyWithIndicator",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: true,
    },
    {
      name: "Created at",
      key: "createdAt",
      dataType: "string",
      formatter: "dateOnly",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: true,
    },
    {
      key: "viewTasksBoardIcon",
      name: "View tasks board",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
  ];

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        const { workflowObjectUuid, workflowObjectType } = d;
        if (workflowObjectUuid) {
          const objectRoute =
            workflowObjectType === "Deal"
              ? getDealListingRoute(workflowObjectUuid, currentUserType)
              : getProjectDetailsByUuid(workflowObjectUuid, currentUserType, { origin: Origin.DashboardPage });
          result.objectLink = {
            text: d.workflowObjectName,
            action: () => {
              navigate(objectRoute);
            },
          } as DataGridButtonLinkCellFormatterData;
        }

        // A general task has no workflow, so there is no tasks board to navigate to.
        const { workflowUuid } = d;
        if (workflowUuid) {
          result.viewTasksBoardIcon = {
            action: () => {
              navigate(getWorkflowTasksBoardRoute(workflowUuid, currentUserType, { origin: Origin.DashboardPage }));
            },
            icon: <ArrowheadRightDarkIcon />,
          } as IconCellFormatterData;
        }
        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.objectLink) {
      filterCriteria.workflowObjectName = filterCriteria.objectLink;
      delete filterCriteria.objectLink;
    }

    const sortKeyMap: Record<string, string> = {
      taskLink: "name",
      avatar: "assignee.name",
      objectLink: "workflowObjectName",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    handleCloseWorkflowTaskPanel,
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
  };
};
